import React, { useState } from "react";
import { Card, Form, Input, Select, Switch, Tabs, Button } from "antd";
import HtmlEditor from "components/editors/HTMLEditor";
import { extractVariables } from "helpers/utils";
import { Card as BootCard } from "react-bootstrap";

const TEMPLATE_CODE_OPTIONS = [
  {
    label: "Authentication & Security",
    options: [
      { value: "OTP_VERIFICATION", label: "OTP verification code" },
      { value: "PASSWORD_RESET", label: "Password reset notification" },
      { value: "LOGIN_ALERT", label: "Login alert" },
    ],
  },
  {
    label: "Account Lifecycle",
    options: [
      { value: "ACCOUNT_CREATED", label: "Account created" },
      { value: "ACCOUNT_APPROVED", label: "Account approved" },
    ],
  },
  {
    label: "Payments & Billing",
    options: [
      { value: "PAYMENT_IN_PROGRESS", label: "Payment in progress" },
      { value: "PAYMENT_SUCCESSFUL", label: "Payment successful" },
      { value: "PAYMENT_FAILED", label: "Payment failed" },
      { value: "PAYMENT_NOTIFICATION", label: "Generic payment notification" },
      { value: "PAYOUT_ISSUED", label: "Payout issued" },
      { value: "PAYOUT_FAILED", label: "Payout failed" },
    ],
  },
  {
    label: "Tasks / Olitask Events",
    options: [
      { value: "TASK_CREATED", label: "Task created" },
      { value: "TASK_UPDATED", label: "Task updated" },
      { value: "TASK_ACCEPTED", label: "Task accepted" },
      { value: "TASK_COMPLETED", label: "Task completed" },
      { value: "TASK_CANCELLED", label: "Task cancelled" },
    ],
  },
  {
    label: "Project Management",
    options: [
      {
        value: "PM_TASK_ACTIVITY_EMAIL",
        label: "PM task activity email",
      },
    ],
  },

  {
    label: "Messaging & Communication",
    options: [
      { value: "NEW_MESSAGE", label: "New message notification" },
      { value: "SYSTEM_ALERT", label: "System alert" },
    ],
  },
  {
    label: "Credit Applications",
    options: [
      {
        value: "CREDIT_APPLICATION_UPDATE_EMAIL",
        label: "Credit application update email",
      },
      {
        value: "CREDIT_APPLICATION_UPDATE_SMS",
        label: "Credit application update SMS",
      },
    ],
  },
];

const CLIENT_APP_OPTIONS = [
  {
    label: "Olitask Mobile UI",
    value: "olitask-mobile-ui",
  },
  {
    label: "Olitask Web UI",
    value: "olitask-web-ui",
  },
  {
    label: "Oliveeb Admin Portal Web UI",
    value: "oliveeb-admin-portal-web-ui",
  },
];

const LOCALE_OPTIONS = [
  {
    label: "English (United States)",
    value: "en-US",
  },
  {
    label: "English (United Kingdom)",
    value: "en-GB",
  },
  {
    label: "Spanish (Spain)",
    value: "es-ES",
  },
];

const switchStyles = (info) => {
  return {
    root: {
      backgroundColor: info.props.checked ? "#00D27A" : "#f0f0f0",
      borderColor: info.props.checked ? "#28a745" : "#d9d9d9",
      borderRadius: "16px",
    },
    handle: {
      backgroundColor: "#fff",
    },
  };
};

export default function MessageTemplateForm({
  initialValues,
  onSubmit,
  submitLabel = "Save Template",
  disableCode = false,
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form] = Form.useForm();

  const handleFinish = async (values) => {
    try {
      setIsSubmitting(true);
      await onSubmit(values);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form
      layout="vertical"
      form={form}
      initialValues={initialValues}
      onFinish={handleFinish}
      style={{ fontFamily: "Inter" }}
    >
      <div className="row g-3 d-flex align-items-stretch">
        {/* LEFT */}
        <div className="col-lg-5">
          <Card title="Template Info" className="custom-shadow">
            <Form.Item
              label="Template Code"
              name="code"
              rules={[{ required: true, message: "Template Code is required" }]}
            >
              <Select
                placeholder="Select template code"
                options={TEMPLATE_CODE_OPTIONS}
                disabled={disableCode}
                showSearch
                popupStyle={{ fontFamily: "Inter" }}
              />
            </Form.Item>

            <Form.Item
              label="Client App"
              name="clientApp"
              rules={[{ required: true, message: "Client App is required" }]}
            >
              <Select
                placeholder="Select client application"
                options={CLIENT_APP_OPTIONS}
                allowClear
                popupStyle={{ fontFamily: "Inter" }}
              />
            </Form.Item>

            <Form.Item
              label="Locale"
              name="locale"
              rules={[{ required: true, message: "Locale is required" }]}
            >
              <Select
                placeholder="Select locale"
                options={LOCALE_OPTIONS}
                allowClear
                popupStyle={{ fontFamily: "Inter" }}
              />
            </Form.Item>

            <Form.Item
              label="Subject"
              name="subject"
              rules={[{ required: true, message: "Subject is required" }]}
            >
              <Input placeholder="Enter template subject" />
            </Form.Item>

            <Form.Item label="Active" name="active" valuePropName="checked">
              <Switch styles={switchStyles} />
            </Form.Item>
          </Card>
        </div>

        {/* RIGHT */}
        <div className="col-lg-7 d-flex">
          <BootCard
            title="Message Content"
            className="custom-shadow flex-fill d-flex flex-column"
            style={{ display: "flex", flexDirection: "column", height: "100%" }}
          >
            <Tabs
              tabBarStyle={{
                padding: "0px 16px",
                paddingTop: "8px",
                borderBottom: "1px solid #E2E8F0",
                marginBottom: "0px",
                fontFamily: "Inter",
              }}
              styles={{
                indicator: {
                  backgroundColor: "#00D27A",
                },
              }}
              style={{ flex: 1, display: "flex", flexDirection: "column" }}
              items={[
                {
                  key: "html",
                  label: "HTML Body",
                  children: (
                    <Form.Item
                      name="bodyHtml"
                      rules={[
                        {
                          required: true,
                          message: "HTML template body is required",
                        },
                      ]}
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        margin: 0,
                      }}
                    >
                      <HtmlEditor
                        variables={extractVariables(
                          Form.useWatch("subject", form) || "",
                        )}
                        style={{ flex: 1, minHeight: 0 }}
                        height={450}
                      />
                    </Form.Item>
                  ),
                },
                {
                  key: "text",
                  label: "Text Body",
                  children: (
                    <Form.Item name="bodyText" style={{ flex: 1, margin: 0 }}>
                      <Input.TextArea
                        style={{ outline: 0, border: 0, height: "485px" }}
                      />
                    </Form.Item>
                  ),
                },
                {
                  key: "preview",
                  label: "Preview",
                  children: (
                    <Form.Item shouldUpdate style={{ flex: 1, margin: 0 }}>
                      {() => (
                        <div
                          dangerouslySetInnerHTML={{
                            __html: form.getFieldValue("bodyHtml"),
                          }}
                        />
                      )}
                    </Form.Item>
                  ),
                },
              ]}
            />
          </BootCard>
        </div>
      </div>

      <div className="mt-3 text-end">
        {isSubmitting ? (
          <Button
            type="primary"
            loading
            iconPlacement="end"
            size="sm"
            className="ms-2 bg-success"
            style={{ fontFamily: "Inter" }}
          >
            Saving
          </Button>
        ) : (
          <Button
            type="primary"
            variant="success"
            htmlType="submit"
            className="bg-success"
          >
            {submitLabel}
          </Button>
        )}
      </div>
    </Form>
  );
}
